import { supabase } from '../lib/supabase';

export const brandService = {
  // Get all brands for current user
  async getAll() {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('brands')?.select(`
        *,
        social_accounts (
          id,
          platform,
          account_name,
          account_url,
          is_connected,
          follower_count,
          last_synced_at
        )
      `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(brand => ({
      id: brand?.id,
      userId: brand?.user_id,
      name: brand?.name,
      description: brand?.description,
      logoUrl: brand?.logo_url,
      website: brand?.website,
      industry: brand?.industry,
      isActive: brand?.is_active,
      createdAt: brand?.created_at,
      updatedAt: brand?.updated_at,
      socialAccounts: brand?.social_accounts?.map(account => ({
        id: account?.id,
        platform: account?.platform,
        accountName: account?.account_name,
        accountUrl: account?.account_url,
        isConnected: account?.is_connected,
        followerCount: account?.follower_count,
        lastSyncedAt: account?.last_synced_at
      }))
    }));
  },

  // Get single brand by ID
  async getById(brandId) {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    // Validate brandId before making the query
    if (!brandId || brandId === ':brandId' || brandId === 'undefined') {
      throw new Error('Invalid brand ID provided');
    }

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('brands')?.select(`
        *,
        social_accounts (
          id,
          platform,
          account_name,
          account_url,
          is_connected,
          follower_count,
          last_synced_at,
          created_at
        )
      `)?.eq('id', brandId)?.eq('user_id', user?.id)?.single();

    if (error) throw error;
    
    if (!data) {
      throw new Error('Brand not found');
    }

    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      description: data?.description,
      logoUrl: data?.logo_url,
      website: data?.website,
      industry: data?.industry,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      socialAccounts: data?.social_accounts?.map(account => ({
        id: account?.id,
        platform: account?.platform,
        accountName: account?.account_name,
        accountUrl: account?.account_url,
        isConnected: account?.is_connected,
        followerCount: account?.follower_count,
        lastSyncedAt: account?.last_synced_at,
        createdAt: account?.created_at
      }))
    };
  },

  // Create new brand
  async create(brandData) {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('brands')?.insert({
        user_id: user?.id,
        name: brandData?.name,
        description: brandData?.description || null,
        logo_url: brandData?.logoUrl || null,
        website: brandData?.website || null,
        industry: brandData?.industry || null,
        is_active: true
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      description: data?.description,
      logoUrl: data?.logo_url,
      website: data?.website,
      industry: data?.industry,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update brand
  async update(brandId, brandData) {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {};
    if (brandData?.name !== undefined) updateData.name = brandData?.name;
    if (brandData?.description !== undefined) updateData.description = brandData?.description;
    if (brandData?.logoUrl !== undefined) updateData.logo_url = brandData?.logoUrl;
    if (brandData?.website !== undefined) updateData.website = brandData?.website;
    if (brandData?.industry !== undefined) updateData.industry = brandData?.industry;
    if (brandData?.isActive !== undefined) updateData.is_active = brandData?.isActive;

    const { data, error } = await supabase?.from('brands')?.update(updateData)?.eq('id', brandId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      description: data?.description,
      logoUrl: data?.logo_url,
      website: data?.website,
      industry: data?.industry,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete brand
  async delete(brandId) {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('brands')?.delete()?.eq('id', brandId)?.eq('user_id', user?.id);

    if (error) throw error;
    return { success: true };
  },

  // Toggle brand active status
  async toggleActive(brandId) {
    if (!supabase) throw new Error('Supabase client not initialized');
    
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current status
    const { data: brand, error: fetchError } = await supabase?.from('brands')?.select('is_active')?.eq('id', brandId)?.eq('user_id', user?.id)?.single();

    if (fetchError) throw fetchError;

    // Toggle status
    const { data, error } = await supabase?.from('brands')?.update({ is_active: !brand?.is_active })?.eq('id', brandId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      isActive: data?.is_active
    };
  }
};
function getBrands(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getBrands is not implemented yet.', args);
  return null;
}

export { getBrands };